import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { UserRole } from "@/apis/bff"

interface AppointmentState {
  selectedWaitingRoomAppointmentId: string | null
  setSelectedWaitingRoomAppointmentId: (selectedWaitingRoomAppointmentId: string | null) => void
  shouldBeSelectedNextAppointmentId: string | null
  setShouldBeSelectedNextAppointmentId: (shouldBeSelectedNextAppointmentId: string | null) => void
  shouldBeSelectedPreviousAppointmentId: string | null
  setShouldBeSelectedPreviousAppointmentId: (shouldBeSelectedPreviousAppointmentId: string | null) => void
  waitingRoomPreviewDialog: {
    isOpening: boolean
    isSharing: boolean
  }
  setWaitingRoomPreviewDialog: (isOpening: boolean, isSharing?: boolean) => void
  isOpenErrorDialog: boolean
  setIsOpenErrorDialog: (isOpenErrorDialog: boolean) => void
  hasAnyUrgentAcuteAppointment: boolean
  setHasAnyUrgentAcuteAppointment: (hasAnyUrgentAcuteAppointment: boolean) => void
  hasScrolledOverFirstUrgentAcuteAppointment: boolean
  setHasScrolledOverFirstUrgentAcuteAppointment: (hasScrolledOverFirstUrgentAcuteAppointment: boolean) => void
}

type AppointmentStore = ReturnType<typeof createAppointmentStore>

const createAppointmentStore = () => {
  return create<AppointmentState>((set) => ({
    selectedWaitingRoomAppointmentId: null,
    setSelectedWaitingRoomAppointmentId: (selectedWaitingRoomAppointmentId) =>
      set({ selectedWaitingRoomAppointmentId }),
    shouldBeSelectedNextAppointmentId: null,
    setShouldBeSelectedNextAppointmentId: (shouldBeSelectedNextAppointmentId) =>
      set({ shouldBeSelectedNextAppointmentId }),
    shouldBeSelectedPreviousAppointmentId: null,
    setShouldBeSelectedPreviousAppointmentId: (shouldBeSelectedPreviousAppointmentId) =>
      set({ shouldBeSelectedPreviousAppointmentId }),
    waitingRoomPreviewDialog: {
      isOpening: false,
      isSharing: false,
    },
    setWaitingRoomPreviewDialog: (isOpening, isSharing = false) =>
      set({ waitingRoomPreviewDialog: { isOpening, isSharing } }),
    isOpenErrorDialog: false,
    setIsOpenErrorDialog: (isOpenErrorDialog) => set({ isOpenErrorDialog }),
    hasAnyUrgentAcuteAppointment: false,
    setHasAnyUrgentAcuteAppointment: (hasAnyUrgentAcuteAppointment) => set({ hasAnyUrgentAcuteAppointment }),
    hasScrolledOverFirstUrgentAcuteAppointment: false,
    setHasScrolledOverFirstUrgentAcuteAppointment: (hasScrolledOverFirstUrgentAcuteAppointment) =>
      set({ hasScrolledOverFirstUrgentAcuteAppointment }),
  }))
}

export const AppointmentContext = createContext<AppointmentStore | null>(null)

type AppointmentProviderProps = React.PropsWithChildren<{ role: UserRole }>

export const AppointmentProvider = ({ children }: AppointmentProviderProps) => {
  const storeRef = useRef<AppointmentStore>(undefined)
  if (!storeRef.current) {
    storeRef.current = createAppointmentStore()
  }

  return <AppointmentContext.Provider value={storeRef.current}>{children}</AppointmentContext.Provider>
}

export function useAppointmentContext<T>(selector: (state: AppointmentState) => T): T {
  const store = useContext(AppointmentContext)
  if (!store) throw new Error("Missing AppointmentContext.Provider in the tree")

  return useStore(store, selector)
}

export const useSelectNextAppointment = () => {
  const setSelectedWaitingRoomAppointmentId = useAppointmentContext(
    (state) => state.setSelectedWaitingRoomAppointmentId,
  )
  const shouldBeSelectedNextAppointmentId = useAppointmentContext((state) => state.shouldBeSelectedNextAppointmentId)

  return () => {
    if (shouldBeSelectedNextAppointmentId) {
      setSelectedWaitingRoomAppointmentId(shouldBeSelectedNextAppointmentId)
    }
  }
}

export const useSelectPreviousAppointment = () => {
  const setSelectedWaitingRoomAppointmentId = useAppointmentContext(
    (state) => state.setSelectedWaitingRoomAppointmentId,
  )
  const shouldBeSelectedPreviousAppointmentId = useAppointmentContext(
    (state) => state.shouldBeSelectedPreviousAppointmentId,
  )

  return () => {
    if (shouldBeSelectedPreviousAppointmentId) {
      setSelectedWaitingRoomAppointmentId(shouldBeSelectedPreviousAppointmentId)
    }
  }
}
